import { useEffect, useId, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { activeMoreItem, MORE_NAV, type NavItem } from "../lib/nav.ts";
import { cx } from "../ui.tsx";

/** "More ▾" at the end of the nav pill: the pages that don't need a slot of their own. On one of them, the button takes its name. */
export function NavMore({ moved, linkClass }: { moved: readonly NavItem[]; linkClass: (s: { isActive: boolean }) => string }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const active = activeMoreItem(pathname, moved);
  const groups = moved.length ? [{ title: "Work", items: moved }, ...MORE_NAV] : MORE_NAV;

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
        className={cx(linkClass({ isActive: Boolean(active) }), "inline-flex items-center gap-1")}
        data-testid="nav-more"
      >
        {active ? active.label : "More"}
        <span aria-hidden className={cx("text-[9px] text-ink-3 transition-transform", open && "rotate-180")}>
          ▾
        </span>
      </button>
      {open && (
        <div id={menuId} className="absolute right-0 top-[calc(100%+14px)] z-50 w-52 rounded-[18px] border border-rule/60 p-2 shadow-lg"
          style={{ background: "var(--card)" }}>
          {groups.map((g) => (
            <div key={g.title} className="py-1">
              <p className="px-3 pb-1 text-[11px] uppercase tracking-wide text-ink-3">{g.title}</p>
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cx("block rounded-xl px-3 py-1.5 text-[13px] transition-colors", isActive ? "bg-paper-2 text-ink" : "text-ink-2 hover:bg-paper-2/60 hover:text-ink")
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
